import { X, AlertTriangle } from 'lucide-react';
import { useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useDeleteTeam } from '@/hooks/useTeams';
import type { TeamForUserDetail } from '@/types/users';

interface DeleteTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: TeamForUserDetail;
  userId?: number;
}

export default function DeleteTeamModal({
  isOpen,
  onClose,
  team,
  userId,
}: DeleteTeamModalProps) {
  const [confirmText, setConfirmText] = useState('');
  const deleteTeam = useDeleteTeam();
  const toast = useToast();

  if (!isOpen) return null;

  const handleClose = () => {
    setConfirmText('');
    onClose();
  };

  const handleSubmit = async () => {
    if (confirmText.trim() !== team.name) return;
    try {
      const res = await deleteTeam.mutateAsync({ teamId: team.id, userId });
      toast.success('Team deleted', res.message);
      handleClose();
    } catch (e) {
      toast.error(
        'Failed to delete team',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  const activeMembers = team.members.filter((m) => m.status === 'active');
  const matches = confirmText.trim() === team.name;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-3 sm:items-center"
      onClick={handleClose}
    >
      <Card
        className="my-auto w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                Delete {team.name}?
              </CardTitle>
              <CardDescription>This is irreversible.</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-destructive/10 border border-destructive rounded-md p-3 space-y-2 text-sm">
            <p className="font-medium text-destructive">
              This affects {activeMembers.length}{' '}
              {activeMembers.length === 1 ? 'member' : 'members'}, not just this
              user:
            </p>
            <ul className="space-y-0.5 text-xs">
              {activeMembers.map((m) => (
                <li key={m.membership_id}>
                  • {m.email} ({m.role})
                </li>
              ))}
            </ul>
            <ul className="text-xs text-muted-foreground pt-2 border-t border-destructive/20 space-y-0.5">
              <li>• The Stripe subscription will be cancelled now</li>
              <li>• The Stripe customer will be deleted</li>
              <li>• All members will be downgraded to the free plan</li>
            </ul>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">
              Type the team name <span className="font-mono">{team.name}</span>{' '}
              to confirm:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={team.name}
              className="w-full px-3 py-2 border rounded-md bg-background"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={handleClose} className="flex-1">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleSubmit}
              disabled={!matches || deleteTeam.isPending}
              className="flex-1"
            >
              {deleteTeam.isPending ? 'Deleting...' : 'Delete team'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
